import { AxiosError } from 'axios';
import { put, call, takeLatest } from 'redux-saga/effects';
import {
  types,
  Integration,
  CreateIntegrationRequest,
  UpdateIntegrationRequest,
  DeleteIntegrationRequest
} from './types';
import { API } from 'services/api/base';
import { getErrorCode } from 'services/api/normalizer';

export function* getIntegrationsAsync() {
  yield put({ type: types.GET_INTEGRATIONS.PENDING, payload: true });

  try {
    const api = new API(`integrations`);
    const response: Integration[] = yield call(api.getAll);

    yield put({ type: types.GET_INTEGRATIONS.SUCCESS, payload: response });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.GET_INTEGRATIONS.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.GET_INTEGRATIONS.ERROR, payload: 'Server error' });
    } else {
      yield put({
        type: types.GET_INTEGRATIONS.ERROR,
        payload: 'Uh oh! Something went wrong. Please try again.'
      });
    }
  } finally {
    yield put({ type: types.GET_INTEGRATIONS.PENDING, payload: false });
  }
}

export function* createIntegrationAsync({ payload }: CreateIntegrationRequest) {
  yield put({ type: types.CREATE_INTEGRATION.PENDING, payload: true });

  try {
    const api = new API(`integrations`);
    yield call(api.create, payload);

    yield put({ type: types.CREATE_INTEGRATION.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.CREATE_INTEGRATION.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.CREATE_INTEGRATION.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.CREATE_INTEGRATION.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.CREATE_INTEGRATION.PENDING, payload: false });
  }
}

export function* updateIntegrationAsync({ payload }: UpdateIntegrationRequest) {
  yield put({ type: types.UPDATE_INTEGRATION.PENDING, payload: true });

  try {
    const api = new API(`integrations`);
    const response: Integration = yield call(api.update, payload);

    yield put({ type: types.UPDATE_INTEGRATION.SUCCESS, payload: response });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.UPDATE_INTEGRATION.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.UPDATE_INTEGRATION.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.UPDATE_INTEGRATION.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.UPDATE_INTEGRATION.PENDING, payload: false });
  }
}

export function* deleteIntegrationAsync({ payload }: DeleteIntegrationRequest) {
  const { id } = payload;
  yield put({ type: types.DELETE_INTEGRATION.PENDING, payload: true });

  try {
    const api = new API(`integrations`);
    yield call(api.destroy, id);

    yield put({ type: types.DELETE_INTEGRATION.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.DELETE_INTEGRATION.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.DELETE_INTEGRATION.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.DELETE_INTEGRATION.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.DELETE_INTEGRATION.PENDING, payload: false });
  }
}

export const sagas = [
  takeLatest(types.GET_INTEGRATIONS.REQUEST, getIntegrationsAsync),
  takeLatest(types.CREATE_INTEGRATION.REQUEST, createIntegrationAsync),
  takeLatest(types.UPDATE_INTEGRATION.REQUEST, updateIntegrationAsync),
  takeLatest(types.DELETE_INTEGRATION.REQUEST, deleteIntegrationAsync)
];
